export const ACCESS_CONTROL_FETCH_SIZE = 10000;

const ACCESS_CONTROL_CLAUSE_REGEX = /(booking_access_control|bookingAccessControl)\s*:\s*\*?\s*(?:"([^"]+)"|'([^']+)'|([^\s)]+))\s*\*?/gi;

const ACCESS_CONTROL_STATUS_MAP = {
  allowed: "Allowed",
  restricted: "Restricted",
  "training required": "Training Required",
  unknown: "Unknown",
};

const BOOK_EQUIPMENT_BASE_STATUS_REGEX = /status\s*:\s*"?active"?|status\s*:\s*"?pending"?/i;
const BOOK_EQUIPMENT_DELETED_STATUS_REGEX = /status\s*:\s*"?deleted"?/gi;
const BOOK_EQUIPMENT_BASE_ENTRY_TYPE_REGEX =
  /entry_type\s*:\s*"?cluster"?(?!-)|entry_type\s*:\s*"?standalone-equipment"?/i;
const BOOK_EQUIPMENT_BASE_CLUSTER_REGEX = /-cluster_id\s*:\s*\*/i;

const DEFAULT_BASE_CLAUSES = {
  statusClause: "(status:Active OR status:Pending)",
  entryTypeClause: '(entry_type:"CLUSTER" OR entry_type:"STANDALONE-EQUIPMENT")',
  clusterClause: "-cluster_id:*",
};

const normalizeAccessControlStatusToken = (rawStatus) => {
  return String(rawStatus || "")
    .toLowerCase()
    .replace(/\\/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const appendClauseWithAnd = (query, clause) => {
  if (!query || !String(query).trim()) {
    return clause;
  }

  return `${query} AND ${clause}`;
};

export const normalizeQueryOperators = (query) => {
  if (typeof query !== "string") {
    return query;
  }

  return query
    .replace(/^\s*\)+\s*/g, "")
    .replace(/\s*\(+\s*$/g, "")
    .replace(/\(\s*(?:(?:AND|OR)\s*)+\)/gi, "")
    .replace(/\(\s*(AND|OR)\s*\)/gi, "")
    .replace(/\(\s*(AND|OR)\s+/gi, "(")
    .replace(/\s+(AND|OR)\s*\)/gi, ")")
    .replace(/\s+(AND|OR)\s+(AND|OR)\s+/gi, " $2 ")
    .replace(/^\s*\(?\s*(AND|OR)\s+/i, "")
    .replace(/\s+(AND|OR)\s*$/i, "")
    .replace(/\(\s*\)/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
};

export const stripDeletedStatusClauses = (query) => {
  if (typeof query !== "string") {
    return query;
  }

  BOOK_EQUIPMENT_DELETED_STATUS_REGEX.lastIndex = 0;
  if (!BOOK_EQUIPMENT_DELETED_STATUS_REGEX.test(query)) {
    return query;
  }

  BOOK_EQUIPMENT_DELETED_STATUS_REGEX.lastIndex = 0;
  return normalizeQueryOperators(
    query.replace(BOOK_EQUIPMENT_DELETED_STATUS_REGEX, "")
  );
};

export const ensureBookEquipmentBaseConstraints = (query) => {
  let nextQuery = normalizeQueryOperators(typeof query === "string" ? query : "");
  const { statusClause, entryTypeClause, clusterClause } = DEFAULT_BASE_CLAUSES;

  nextQuery = stripDeletedStatusClauses(nextQuery) || "";

  if (!BOOK_EQUIPMENT_BASE_STATUS_REGEX.test(nextQuery)) {
    nextQuery = appendClauseWithAnd(nextQuery, statusClause);
  }

  if (!BOOK_EQUIPMENT_BASE_ENTRY_TYPE_REGEX.test(nextQuery)) {
    nextQuery = appendClauseWithAnd(nextQuery, entryTypeClause);
  }

  if (!BOOK_EQUIPMENT_BASE_CLUSTER_REGEX.test(nextQuery)) {
    nextQuery = appendClauseWithAnd(nextQuery, clusterClause);
  }

  return normalizeQueryOperators(nextQuery);
};

export const extractAccessControlStatuses = (query) => {
  if (typeof query !== "string") {
    return [];
  }

  ACCESS_CONTROL_CLAUSE_REGEX.lastIndex = 0;
  const foundStatuses = new Set();
  let match;

  while ((match = ACCESS_CONTROL_CLAUSE_REGEX.exec(query)) !== null) {
    const fullClause = String(match[0] || "");

    // Treat text-search wildcard clauses as non-binding; only checkbox enum values should drive filtering.
    if (fullClause.includes("*")) {
      continue;
    }

    const rawStatus = match[2] ?? match[3] ?? match[4] ?? "";
    const normalizedStatus = normalizeAccessControlStatusToken(String(rawStatus).replace(/\*/g, ""));
    const mappedStatus = ACCESS_CONTROL_STATUS_MAP[normalizedStatus];

    if (mappedStatus) {
      foundStatuses.add(mappedStatus);
    }
  }

  return Array.from(foundStatuses);
};

export const getRequestedAccessControlStatuses = (searchValue, filterQuery) => {
  return new Set([
    ...extractAccessControlStatuses(searchValue),
    ...extractAccessControlStatuses(filterQuery),
  ]);
};

export const stripAccessControlFilterClauses = (query) => {
  if (!query || typeof query !== "string") {
    return query;
  }

  ACCESS_CONTROL_CLAUSE_REGEX.lastIndex = 0;
  if (!ACCESS_CONTROL_CLAUSE_REGEX.test(query)) {
    return query;
  }

  ACCESS_CONTROL_CLAUSE_REGEX.lastIndex = 0;
  const strippedQuery = query.replace(ACCESS_CONTROL_CLAUSE_REGEX, "");

  return normalizeQueryOperators(strippedQuery);
};
